/**
 * Client Care - Terms of Business.
 *
 * The document is held here as data so the wording can be revised without
 * touching component code. Bump CARE_LETTER_VERSION whenever the wording
 * changes -- the accepted version is recorded in the submission email, so
 * we can always tell which terms a given client agreed to.
 */

export const CARE_LETTER_VERSION = '2.0';

/** Marker for wording that has not been supplied yet. See isCareLetterComplete(). */
export const PLACEHOLDER = '[TO BE SUPPLIED]';

export const documentTitle = 'Client Care - Terms of Business';

export const company = {
  displayName: 'Lossos & Co Ltd - The LPA Company',
  phone: '0330 133 4433',
  phoneHref: 'tel:03301334433',
  email: 'info@theLPAcompany.com',
  emailHref: 'mailto:info@theLPAcompany.com',
};

export const intro = [
  'Thank you for choosing The LPA Company to assist you with the preparation of your Lasting Power of Attorney (LPA).',
  'This document sets out the basis on which we will provide our services, the terms of our engagement, and the responsibilities of both parties. Please read this document carefully as well as the confirmation at the end before we begin work on your behalf.',
];

/**
 * Block types:
 *   { type: 'p', text }               paragraph
 *   { type: 'p', parts: [...] }       paragraph containing an inline link
 *   { type: 'ul', items: [...] }      bulleted list; an item may be a string
 *                                     or { parts: [...] } for inline links
 *   { type: 'note', text }            emphasised callout
 *   { type: 'address', lines: [...] } postal address
 *   { type: 'upload' }                the identity-document upload controls,
 *                                     rendered by CareLetter (see uploadFields)
 *
 * A link part is { link, text } for an internal path or { href, text } for an
 * external/mailto/tel target. Both open in a new tab so a client part-way
 * through the document does not lose their place.
 */
export const clauses = [
  {
    n: 1,
    title: 'Our Services',
    blocks: [
      { type: 'p', text: 'The LPA Company provides assistance with the administrative preparation of Lasting Power of Attorney applications.' },
      { type: 'p', text: 'Our services may include:' },
      {
        type: 'ul',
        items: [
          'Providing guidance regarding Lasting Power of Attorney applications',
          'Collecting the information required to prepare the LPA documentation',
          'Preparing the official LPA forms for review and signature',
          'Checking documentation for accuracy and completeness',
          'Providing instructions for signing and witnessing the documents',
          'Assisting with submission of the application to the Office of the Public Guardian (OPG) where requested',
        ],
      },
      { type: 'note', text: 'Please note that we are not acting as solicitors unless expressly agreed in writing. Our role is to provide administrative support and guidance relating to the preparation of LPA documentation.' },
      { type: 'p', text: 'The final decision regarding registration of an LPA is made by the Office of the Public Guardian.' },
    ],
  },
  {
    n: 2,
    title: 'Identity Verification (AML Requirements)',
    blocks: [
      { type: 'p', text: 'Before we are able to begin work, we are required to verify your identity in accordance with anti-money laundering and compliance obligations.' },
      { type: 'p', text: 'You will therefore be required to provide:' },
      { type: 'p', text: 'One form of photographic identification, such as:' },
      { type: 'ul', items: ['Passport', 'Driving licence', 'National identity card'] },
      { type: 'p', text: 'and' },
      { type: 'p', text: 'One proof of address dated within the last 3 months:' },
      {
        type: 'ul',
        items: [
          'Utility bill',
          'Bank statement',
          'Council tax statement',
          'HMRC correspondence',
          'Mortgage statement',
        ],
      },
      { type: 'p', text: 'Documents can be uploaded here:' },
      { type: 'upload' },
      { type: 'p', text: 'Or, if you prefer:' },
      {
        type: 'ul',
        items: [
          {
            parts: [
              { text: 'Good quality scans or photographs can also be sent via email to ' },
              { href: 'mailto:info@thelpacompany.com', text: 'info@thelpacompany.com' },
              { text: '; or' },
            ],
          },
          {
            parts: [
              { text: 'Sent via whatsapp to ' },
              { href: 'https://wa.me/message/O2E665HYYHONE1', text: '0330 133 4433' },
            ],
          },
        ],
      },
      { type: 'p', text: 'Please contact us should you wish to share hard copies by post.' },
      { type: 'p', text: 'We reserve the right to request additional documentation if required for compliance purposes.' },
    ],
  },
  {
    n: 3,
    title: 'Fees',
    blocks: [
      {
        type: 'p',
        parts: [
          { text: 'Our service fees for preparing your LPA application are stated on our website ' },
          { href: 'https://thelpacompany.com/lpa-fees', text: 'https://thelpacompany.com/lpa-fees' },
          { text: ' or as agreed in writing between the parties. This fee covers our administrative services relating to the preparation of your LPA.' },
        ],
      },
      { type: 'p', text: 'Please note:' },
      {
        type: 'ul',
        items: [
          'Our fee does not include the Government registration fees which is charged by the Office of the Public Guardian.',
          'The current registration fee is £92 per LPA (subject to change). This is itemised separately on our invoice.',
          'Certain applicants may qualify for a fee reduction or exemption depending on financial circumstances or the receipt of means-tested benefits. (Please contact us to discuss your circumstances).',
          'Should you need to make changes to your LPA application once it is submitted or if the Office of Public Guardian rejects your application, a further registration fee may be payable.',
          'There is currently no VAT payable on our services or the OPG fees.',
          'Certification services are available at £75 per LPA document.',
          'Additional certified copies of documents are available at a cost of £15/copy.',
        ],
      },
      { type: 'p', text: 'Unless otherwise agreed, all invoiced fees must be paid in full prior to the submission of LPA documents for registration.' },
    ],
  },
  {
    n: 4,
    title: 'Distance Selling Regulations',
    blocks: [
      { type: 'p', text: 'Where our services are arranged by telephone, online, or without a face-to-face meeting, the agreement is governed by the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013.' },
      { type: 'p', text: 'You therefore have the right to cancel this agreement within 14 days from the date you sign this agreement without giving any reason.' },
    ],
  },
  {
    n: 5,
    title: 'Cooling-Off Period',
    blocks: [
      { type: 'p', text: 'You have a 14-day cooling-off period during which you may cancel this agreement.' },
      { type: 'p', text: 'If you wish to cancel, you must notify us in writing by email or letter within this period.' },
      { type: 'p', text: 'If you request below that we begin work within the cooling-off period, you acknowledge that:' },
      {
        type: 'ul',
        items: [
          'work may begin immediately',
          'if you later cancel, your 50% payment for preparation of the application will become non-refundable.',
        ],
      },
      { type: 'p', text: 'If the service has been fully completed within the cooling-off period, your right to cancel may no longer apply.' },
    ],
  },
  {
    n: 6,
    title: 'Cancellation & Refund Policy',
    blocks: [
      { type: 'p', text: 'If you cancel your instructions after work has begun, your 50% payment for preparation of the application will become non-refundable.' },
      { type: 'p', text: 'Once LPA documentation has been prepared and issued, or where an application has been submitted to the Office of the Public Guardian, fees will be fully non-refundable.' },
      { type: 'p', text: 'Government registration fees paid to the Office of the Public Guardian are non-refundable once the application has been submitted.' },
    ],
  },
  {
    n: 7,
    title: 'Client Responsibilities',
    blocks: [
      { type: 'p', text: 'By engaging our services you confirm that:' },
      {
        type: 'ul',
        items: [
          'the information you provide is accurate and complete',
          'you have the mental capacity to create a Lasting Power of Attorney',
          'you are making the LPA voluntarily and without undue influence',
          'you understand the purpose and effect of the LPA',
        ],
      },
      { type: 'p', text: 'We cannot be held responsible for delays or errors caused by incorrect or incomplete information provided by the client.' },
    ],
  },
  {
    n: 8,
    title: 'Limitation of Liability',
    blocks: [
      { type: 'p', text: 'While we will take reasonable care in preparing your documentation, Lossos & Co Ltd (trading as The LPA Company) shall not be liable for any loss arising from:' },
      {
        type: 'ul',
        items: [
          'inaccurate information provided by the client',
          'delays or decisions made by the Office of the Public Guardian',
          'delays caused by third parties involved in the process',
          'failure of the client or other parties to sign or return documents in a timely manner',
        ],
      },
      { type: 'p', text: 'Our liability, where permitted by law, shall be limited to the amount paid for the services provided.' },
      { type: 'p', text: 'Nothing in this agreement limits liability where it would be unlawful to do so.' },
    ],
  },
  {
    n: 9,
    title: 'Confidentiality & Data Protection',
    blocks: [
      { type: 'p', text: 'Lossos & Co Ltd (trading as The LPA Company) will treat all client information as confidential.' },
      { type: 'p', text: 'Your personal information will be processed in accordance with UK data protection legislation, including the UK General Data Protection Regulation (UK GDPR).' },
      { type: 'p', text: 'Your information may be shared with relevant parties where necessary to provide the service, including:' },
      {
        type: 'ul',
        items: [
          'the Office of the Public Guardian',
          'identity verification providers',
          'authorised service partners',
        ],
      },
    ],
  },
  {
    n: 10,
    title: 'Complaints Procedure',
    blocks: [
      { type: 'p', text: 'We aim to provide a high standard of service to all clients.' },
      { type: 'p', text: 'If you are dissatisfied with any aspect of our service, please contact us in writing at:' },
      {
        type: 'address',
        lines: [
          'Lossos & Co Ltd',
          'First Floor, Winston House, 349 Regents Park Road, London N3 1DH',
        ],
      },
      {
        type: 'p',
        parts: [
          { text: 'or via email to ' },
          { href: 'mailto:info@theLPAcompany.com', text: 'info@theLPAcompany.com' },
        ],
      },
      { type: 'p', text: 'We will acknowledge your complaint and aim to respond within 14 working days.' },
    ],
  },
  {
    n: 11,
    title: 'Governing Law',
    blocks: [
      { type: 'p', text: 'This agreement shall be governed by and interpreted in accordance with the laws of England and Wales.' },
    ],
  },
  {
    n: 12,
    title: 'Authority to Act',
    blocks: [
      { type: 'p', text: 'By accepting this document, you authorise Lossos & Co Ltd (trading as The LPA Company) to act on your behalf in relation to the preparation and submission of your Lasting Power of Attorney (LPA) application.' },
      { type: 'p', text: 'This authority includes permission to:' },
      {
        type: 'ul',
        items: [
          'prepare the LPA documentation on your behalf',
          'communicate with the Office of the Public Guardian regarding your application',
          'provide instructions relating to the completion of the LPA forms',
          'submit documentation required for the registration of the LPA where authorised by you',
        ],
      },
      { type: 'p', text: 'You hereby confirm that:' },
      {
        type: 'ul',
        items: [
          'Lossos & Co Ltd is assisting with the administrative preparation of your LPA',
          'the Office of the Public Guardian is responsible for registering the LPA',
          'You remain responsible for reviewing and signing the final documents before submission',
        ],
      },
    ],
  },
  {
    n: 13,
    title: 'Client Declaration & Acceptance',
    blocks: [
      { type: 'p', text: 'By completing and submitting this form, you confirm that:' },
      {
        type: 'ul',
        items: [
          'you have the mental capacity to create a Lasting Power of Attorney and are providing this authority voluntarily;',
          'you have read and understood and agree to this Client Care Terms of Business;',
          'you agree to instruct Lossos & Co Ltd (trading as The LPA Company) to assist with your Lasting Power of Attorney application;',
          'you understand the cancellation and cooling-off rights described above;',
          'you consent to identity verification and the processing of your personal data for the purposes of providing this service; and',
          'you have read and understood, and agree to, the Authority to Act contained in clause 12.',
        ],
      },
    ],
  },
];

/**
 * Opt-in to work starting inside the cooling-off period. In the source
 * document this is the final bullet of clause 13; it is rendered as a
 * checkbox at the top of the signature block, immediately after that clause.
 */
export const earlyStartDeclaration =
  'You request that The LPA Company begins work on your instructions immediately, before the expiry of the 14-day cancellation period. You understand that if you subsequently cancel after work has begun, the payment referred to in clause 5 may become non-refundable.';

/**
 * The two identity documents clause 2 asks for. Each becomes one Web3Forms
 * attachment field; `key` is the field name in the submission.
 */
export const uploadFields = [
  {
    key: 'photo_id',
    id: 'cl-upload-photo-id',
    label: 'Photographic identification',
    hint: 'Passport, driving licence or national identity card',
    emailLabel: 'Photographic ID',
  },
  {
    key: 'proof_of_address',
    id: 'cl-upload-proof-address',
    label: 'Proof of address',
    hint: 'Dated within the last 3 months',
    emailLabel: 'Proof of address',
  },
];

export const signatureFields = {
  clientNamesLabel: 'Clients Full Name(s)',
  clientNamesHint: 'Include names of all donors/clients here',
  signatureLabel: 'Signature',
  signatoryLabel: 'Name of person signing',
  dateLabel: 'Date',
  submitLabel: 'Accept & Submit',
};

function blockHasPlaceholder(block) {
  const inItem = item =>
    typeof item === 'string'
      ? item.includes(PLACEHOLDER)
      : (item.parts || []).some(p => (p.text || '').includes(PLACEHOLDER));

  if (block.type === 'ul') return block.items.some(inItem);
  if (block.type === 'address') return block.lines.some(l => l.includes(PLACEHOLDER));
  if (block.parts) return block.parts.some(p => (p.text || '').includes(PLACEHOLDER));
  return (block.text || '').includes(PLACEHOLDER);
}

/** True when every clause has real wording (no PLACEHOLDER left). */
export function isCareLetterComplete() {
  return !clauses.some(clause => clause.blocks.some(blockHasPlaceholder));
}

/** Clause numbers still awaiting wording, e.g. [4]. */
export function incompleteClauseNumbers() {
  return clauses.filter(c => c.blocks.some(blockHasPlaceholder)).map(c => c.n);
}
